'use client'

import { Card, Cell, Checkbox, Container, Spacer, Text } from '@vinted/web-ui'
import { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import {
  AccessChannel,
  FaqEntryType,
} from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import {
  itemUploadClickEvent,
  userInputEvent,
  getBumpMinimumPrice,
  transformBumpMinimumPriceResponse,
  getBumpMinimumPriceSvcVas,
  transformBumpMinimumPriceSvcVasResponse,
} from '@marketplace-web/vas/vas-data'
import {
  ItemUploadFieldName,
  ItemStatus,
  UiStateContext,
  useAttributesContext,
  useFieldVisibility,
} from '@marketplace-web/item-upload/item-upload-data'

import BumpValuePropositionModalV3 from '../../Bumps/BumpCheckout/BumpValuePropositionV3'

type Props = { tempUuid: string }

const BumpItemCheckbox = ({ tempUuid }: Props) => {
  const { track } = useTracking()
  const { locale } = useIntl()
  const {
    screen,
    itemStatus: [itemStatus],
    canBumpItem: [canBumpItem],
  } = useContext(UiStateContext)
  const { attributes, isBumpChecked, setIsBumpChecked } = useAttributesContext()
  const isOnHoliday = useSession().user?.is_on_holiday
  const trackExpose = useTrackAbTestCallback()
  const vasApiGatewaySwapBumpMinimumPriceAbTest = useAbTest(
    'vas_api_gateway_swap_bump_minimum_price',
  )
  const isVasApiGatewaySwapBumpMinimumPriceAbTestOn =
    vasApiGatewaySwapBumpMinimumPriceAbTest?.variant === 'on'

  const bumpValuePropositionInListingFormAbTest = useAbTest(
    'bump_value_proposition_in_listing_form',
  )

  const canShowBumpCheckbox =
    attributes.price !== null && (itemStatus !== ItemStatus.Edit || canBumpItem) && !isOnHoliday

  const [showValuePropositionModal, setShowValuePropositionModal] = useState(false)

  const {
    fetch: fetchBumpMinimumPriceCore,
    transformedData: minimumPriceOptionCore,
    isLoading: isLoadingCore,
  } = useFetch(getBumpMinimumPrice, transformBumpMinimumPriceResponse)

  const {
    fetch: fetchBumpMinimumPriceSvcVas,
    transformedData: minimumPriceOptionSvcVas,
    isLoading: isLoadingSvcVas,
  } = useFetch(getBumpMinimumPriceSvcVas, transformBumpMinimumPriceSvcVasResponse)

  const minimumPriceOption = isVasApiGatewaySwapBumpMinimumPriceAbTestOn
    ? minimumPriceOptionSvcVas
    : minimumPriceOptionCore
  const isLoading = isVasApiGatewaySwapBumpMinimumPriceAbTestOn ? isLoadingSvcVas : isLoadingCore
  const fetchBumpMinimumPrice = isVasApiGatewaySwapBumpMinimumPriceAbTestOn
    ? fetchBumpMinimumPriceSvcVas
    : fetchBumpMinimumPriceCore

  const translate = useTranslate('item_upload')
  const translateA11y = useTranslate('bump_items.a11y')

  useFieldVisibility({
    fieldName: ItemUploadFieldName.Bump,
    isVisible: canShowBumpCheckbox,
  })

  const debouncedFetchBumpMinimumPrice = useDebounce(fetchBumpMinimumPrice, 1000, false)

  useEffect(() => {
    if (!canShowBumpCheckbox || !attributes.price) return

    debouncedFetchBumpMinimumPrice(attributes.price)
    trackExpose(vasApiGatewaySwapBumpMinimumPriceAbTest)
  }, [
    debouncedFetchBumpMinimumPrice,
    canShowBumpCheckbox,
    attributes.price,
    trackExpose,
    vasApiGatewaySwapBumpMinimumPriceAbTest,
  ])

  function handleCheckboxClick() {
    track(
      userInputEvent({
        screen,
        value: Number(!isBumpChecked).toString(),
        state: 'focus',
        tempUuid,
        target: ItemUploadFieldName.Bump,
      }),
    )
    setIsBumpChecked(!isBumpChecked)
  }

  function handleLearnMoreClick() {
    if (bumpValuePropositionInListingFormAbTest?.variant === 'on') {
      setShowValuePropositionModal(true)
    }

    track(
      itemUploadClickEvent({
        screen,
        target: 'push_up_learn_more',
        uploadSessionId: tempUuid,
      }),
    )
    trackExpose(bumpValuePropositionInListingFormAbTest)
  }

  const renderCheckbox = () => {
    if (isLoading) {
      return (
        <ContentLoader styling={ContentLoader.Styling.Narrow} size={ContentLoader.Size.Large} />
      )
    }

    if (!minimumPriceOption) return null

    return (
      <Container>
        <Checkbox
          name={ItemUploadFieldName.Bump}
          checked={isBumpChecked}
          onClick={handleCheckboxClick}
          testId="bump-checkbox"
          aria={{ 'aria-labelledby': 'bump-title' }}
          onChange={() => undefined}
        />
      </Container>
    )
  }

  const renderLearnMoreLink = () => {
    if (bumpValuePropositionInListingFormAbTest?.variant === 'on') {
      return (
        <button onClick={handleLearnMoreClick} type="button" key="learn-more-button">
          <Text as="span" text={translate('bump.learn_more')} type="subtitle" underline clickable />
        </button>
      )
    }

    return (
      <HelpCenterFaqEntryUrl
        type={FaqEntryType.Bump}
        accessChannel={AccessChannel.ProductLink}
        render={url => (
          <a href={url} target="_blank" rel="noreferrer" onClick={handleLearnMoreClick}>
            <Text
              as="span"
              text={translate('bump.learn_more')}
              type="subtitle"
              width="parent"
              clickable
            />
          </a>
        )}
      />
    )
  }

  const renderTitle = () => {
    const title = minimumPriceOption?.hasFreeBump
      ? translate('free_bump.header')
      : translate('bump.header_v2')

    return <Text as="span" text={title} type="title" width="parent" id="bump-title" />
  }

  const renderPrice = () => {
    if (!minimumPriceOption) return null

    const price = formatCurrencyAmount(
      {
        ...minimumPriceOption.payable,
        amount: minimumPriceOption.hasFreeBump ? '0' : minimumPriceOption.payable.amount,
      },
      locale,
    )

    const hasDiscount = parseFloat(minimumPriceOption.discount.amount) > 0
    const isOriginalPriceVisible = minimumPriceOption.hasFreeBump || hasDiscount

    const priceText = minimumPriceOption.hasFreeBump ? price : translate('bump.price', { price })
    const originalPriceText = isOriginalPriceVisible
      ? translateA11y('price', {
          price: undefined,
          original_price: formatCurrencyAmount(minimumPriceOption.total, locale),
        })
      : ''

    return (
      <div aria-label={priceText + originalPriceText}>
        <Spacer size="small" />
        <Text
          text={priceText}
          as="p"
          type="subtitle"
          theme="amplified"
          aria={{ 'aria-hidden': 'true' }}
        />
        {isOriginalPriceVisible && (
          <>
            <Spacer size="small" orientation="vertical" />
            <Text
              text={formatCurrencyAmount(minimumPriceOption.total, locale)}
              as="p"
              type="subtitle"
              strikethrough
              theme="muted"
              aria={{ 'aria-hidden': 'true' }}
            />
          </>
        )}
        <Spacer size="small" />
      </div>
    )
  }

  if (!canShowBumpCheckbox) return null

  return (
    <>
      <Spacer size="x-large" />
      <Card>
        <Container styling="wide">
          <Cell styling="tight" title={renderTitle()} suffix={renderCheckbox()}>
            {renderPrice()}
            <Text type="subtitle" text={translate('bump.body')} as="p" width="parent" />
            {renderLearnMoreLink()}
          </Cell>
        </Container>
      </Card>
      <BumpValuePropositionModalV3
        show={showValuePropositionModal}
        showCloseButton={false}
        onCloseOrConfirm={() => setShowValuePropositionModal(false)}
      />
    </>
  )
}

export default BumpItemCheckbox
